import type { NormalizedFeature } from './geocoder.js';

const BING_HOSTS = new Set(['bing.com', 'www.bing.com', 'cn.bing.com']);
const SHORT_GOOGLE_HOSTS = new Set(['maps.app.goo.gl', 'goo.gl']);
const GOOGLE_HOST_PATTERN = /^(?:(?:www|maps)\.)?google\.[a-z0-9-]{2,63}(?:\.[a-z0-9-]{2,63})?$/i;

function isGoogleHost(host: string): boolean {
  return GOOGLE_HOST_PATTERN.test(host);
}

function parseGoogleMapsUrl(url: URL): NormalizedFeature | null {
  // Extract place name from path: /maps/place/Eiffel+Tower/@lat,lon,zoom
  const placeMatch = url.pathname.match(/\/place\/([^/@]+)\/@/);
  const name = placeMatch ? decodeURIComponent(placeMatch[1].replace(/\+/g, ' ')) : null;

  // @lat,lon,zoom in pathname
  const atMatch = url.pathname.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*),/);
  if (atMatch) {
    const lat = Number(atMatch[1]);
    const lon = Number(atMatch[2]);
    if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
      return { properties: { lat, lon, formatted: name ?? `${lat}, ${lon}` } };
    }
  }

  // ?q=lat,lng or ?ll=lat,lng
  const q = url.searchParams.get('q') ?? url.searchParams.get('ll');
  if (q) {
    const m = q.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
    if (m) {
      const lat = Number(m[1]);
      const lon = Number(m[2]);
      if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        return { properties: { lat, lon, formatted: `${lat}, ${lon}` } };
      }
    }
  }

  return null;
}

function parseBingMapsUrl(url: URL): NormalizedFeature | null {
  const ppois = url.searchParams.get('ppois');
  if (ppois) {
    // Format: lat_lon_Name_~ (name is URL-encoded)
    const m = ppois.match(/^(-?\d+\.?\d+)_(-?\d+\.?\d+)_([^_]*)_/);
    if (m) {
      const lat = Number(m[1]);
      const lon = Number(m[2]);
      const name = decodeURIComponent(m[3]) || `${lat}, ${lon}`;
      if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        return { properties: { lat, lon, formatted: name } };
      }
    }
  }
  return null;
}

async function expandShortUrl(shortUrl: string): Promise<string | null> {
  try {
    const res = await fetch(`/api/resolve-url?url=${encodeURIComponent(shortUrl)}`);
    if (!res.ok) return null;
    // SAFETY: The following parser checks every field that it accepts from this external payload.
    const data = await res.json() as { url: string };
    return data.url ?? null;
  } catch {
    return null;
  }
}

export async function parseMapsUrl(query: string): Promise<NormalizedFeature | null> {
  if (!query.startsWith('http')) return null;

  let url: URL;
  try {
    url = new URL(query);
  } catch {
    return null;
  }

  const host = url.hostname;

  if (SHORT_GOOGLE_HOSTS.has(host)) {
    const expanded = await expandShortUrl(query);
    if (!expanded || expanded === query) return null;
    return parseMapsUrl(expanded);
  }

  if (isGoogleHost(host)) return parseGoogleMapsUrl(url);
  if (BING_HOSTS.has(host)) return parseBingMapsUrl(url);

  return null;
}
